import React, { useState } from "react"
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'

import { httpOk, GET } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import AnalyticsFilterBar, { FilterField, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const ABC_SUMMARY_META = {
	A: { title: "Категория A", bg: "var(--dark-tint-success, #e7f8ef)", border: "var(--dark-tint-border-success, #c8efd9)", color: "var(--dark-fg-success, #1f9d57)", icon: "uil-chart-growth" },
	B: { title: "Категория B", bg: "var(--dark-tint-warning, #fdf6e3)", border: "var(--dark-tint-border-warning, #f5e4b8)", color: "var(--dark-fg-warning, #e0762c)", icon: "uil-minus" },
	C: { title: "Категория C", bg: "var(--dark-tint-danger, #fdecec)", border: "var(--dark-tint-border-danger, #f5d0d0)", color: "var(--dark-fg-danger, #d23434)", icon: "uil-chart-down" },
}

const GROUP_CLASS = {
	A: "bg-success text-white", B: "bg-warning text-white", C: "bg-danger text-white",
	X: "bg-success text-white", Y: "bg-warning text-white", Z: "bg-danger text-white",
}

const detailOptions = [
	{ id: 'product', name: 'Блюдо' },
	{ id: 'menu', name: 'Меню' },
	{ id: 'category', name: 'Категория' },
]

const intervalOptions = [
	{ id: 'day', name: 'День' },
	{ id: 'week', name: 'Неделя' },
	{ id: 'month', name: 'Месяц' },
]

const tabs = [
	{ id: 'ABC', label: 'ABC' },
	{ id: 'XYZ', label: 'XYZ' },
	{ id: 'ABC-XYZ', label: 'ABC / XYZ' },
]

function AbcSummaryCards({ summary }) {
	const items = summary?.length
		? summary
		: ['A', 'B', 'C'].map(category => ({ category, count: 0, share_percent: 0, amount: 0 }))

	return (
		<div className="row g-3 mb-3">
			{items.map((item) => {
				const meta = ABC_SUMMARY_META[item.category] || ABC_SUMMARY_META.C
				return (
					<div className="col-md-4" key={item.category}>
						<div className="h-100" style={{ ...cardStyle, background: meta.bg, borderColor: meta.border, padding: "16px 18px" }}>
							<div className="d-flex align-items-center gap-2 mb-2">
								<i className={`uil ${meta.icon}`} style={{ fontSize: "18px", color: meta.color }}></i>
								<span style={{ fontWeight: 600, color: "var(--dark-text, #374151)" }}>{meta.title} по выручке</span>
							</div>
							<div className="d-flex align-items-baseline gap-2 flex-wrap">
								<span style={{ fontSize: "28px", fontWeight: 800, color: meta.color, lineHeight: 1 }}>{item.count}</span>
								<span style={mutedStyle}>позиций ({formatMoney(item.share_percent, 1)}%)</span>
							</div>
							<div className="mt-2" style={{ fontWeight: 700, color: "var(--dark-text-strong, #111827)" }}>
								{formatMoney(item.amount, 0)}
							</div>
						</div>
					</div>
				)
			})}
		</div>
	)
}

// Сводка ABC (по выручке) × XYZ: сколько позиций в каждой клетке
function AbcXyzMatrix({ rows }) {
	const counts = {}
	rows.forEach(row => {
		if (!row.xyz_category) return
		const key = `${row.abc_sales}${row.xyz_category}`
		counts[key] = (counts[key] || 0) + 1
	})
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-1">ABC × XYZ</h5>
				<div style={mutedStyle} className="mb-2">Строки — вклад в выручку, столбцы — стабильность продаж</div>
				<table className="table table-bordered text-center mb-0" style={{ maxWidth: "420px" }}>
					<thead>
						<tr><th></th><th>X</th><th>Y</th><th>Z</th></tr>
					</thead>
					<tbody>
						{['A', 'B', 'C'].map(abc => (
							<tr key={abc}>
								<th>{abc}</th>
								{['X', 'Y', 'Z'].map(xyz => <td key={xyz}>{counts[`${abc}${xyz}`] || 0}</td>)}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
}

function ThresholdsModal({ show, applied, onCancel, onSave }) {
	const [form, setForm] = useState(null)
	const values = form || { abc_a: applied.abc_a, abc_b: applied.abc_b, xyz_x: applied.xyz_x, xyz_y: applied.xyz_y }
	const abcC = 100 - Number(values.abc_a) - Number(values.abc_b)
	const abcInvalid = !(Number(values.abc_a) > 0) || Number(values.abc_b) < 0 || abcC <= 0
	const xyzInvalid = !(Number(values.xyz_x) >= 0) || Number(values.xyz_x) >= Number(values.xyz_y)

	function patchForm(patch) {
		setForm({ ...values, ...patch })
	}

	// Отмена — черновик порогов выбрасывается, в отчёте остаются применённые
	function cancel() {
		setForm(null)
		onCancel()
	}

	function save() {
		onSave({
			abc_a: Number(values.abc_a),
			abc_b: Number(values.abc_b),
			xyz_x: Number(values.xyz_x),
			xyz_y: Number(values.xyz_y),
		})
		setForm(null)
	}

	const inputStyle = { height: "30px", width: "70px" }

	return (
		<Modal show={show} animation={false} centered backdrop="static" onHide={cancel}>
			<Modal.Header closeButton>
				<Modal.Title>Пороги групп</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
				<h6>ABC — доля в накопленном итоге</h6>
				<div className="d-flex flex-column gap-2 mb-3">
					<div className="d-flex justify-content-between align-items-center">
						<span><b>A</b> — первые позиции, дающие</span>
						<span className="d-flex align-items-center gap-1">
							<input type="number" className="form-control text-end" style={inputStyle} value={values.abc_a} onChange={(e) => patchForm({ abc_a: e.target.value })} />%
						</span>
					</div>
					<div className="d-flex justify-content-between align-items-center">
						<span><b>B</b> — следующие</span>
						<span className="d-flex align-items-center gap-1">
							<input type="number" className="form-control text-end" style={inputStyle} value={values.abc_b} onChange={(e) => patchForm({ abc_b: e.target.value })} />%
						</span>
					</div>
					<div className="d-flex justify-content-between align-items-center">
						<span><b>C</b> — остаток</span>
						<b>{abcInvalid ? '—' : `${abcC}%`}</b>
					</div>
					{abcInvalid && <div className="text-danger" style={{ fontSize: "12px" }}>A и B вместе должны быть меньше 100%</div>}
				</div>

				<h6>XYZ — коэффициент вариации продаж по интервалам</h6>
				<div className="d-flex flex-column gap-2">
					<div className="d-flex justify-content-between align-items-center">
						<span><b>X</b> — стабильные, КВ не больше</span>
						<span className="d-flex align-items-center gap-1">
							<input type="number" className="form-control text-end" style={inputStyle} value={values.xyz_x} onChange={(e) => patchForm({ xyz_x: e.target.value })} />%
						</span>
					</div>
					<div className="d-flex justify-content-between align-items-center">
						<span><b>Y</b> — колеблющиеся, КВ не больше</span>
						<span className="d-flex align-items-center gap-1">
							<input type="number" className="form-control text-end" style={inputStyle} value={values.xyz_y} onChange={(e) => patchForm({ xyz_y: e.target.value })} />%
						</span>
					</div>
					<div className="d-flex justify-content-between align-items-center">
						<span><b>Z</b> — нерегулярные</span>
						<b>КВ больше {values.xyz_y}%</b>
					</div>
					{xyzInvalid && <div className="text-danger" style={{ fontSize: "12px" }}>Порог X должен быть меньше порога Y</div>}
				</div>

				<div className="d-flex w-100 mt-3 gap-2">
					<button type="button" className="btn btn-outline-secondary w-100" onClick={cancel}>Отмена</button>
					<button type="button" className="btn btn-primary w-100" disabled={abcInvalid || xyzInvalid} onClick={save}>Применить</button>
				</div>
			</Modal.Body>
		</Modal>
	)
}

function Index() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState({ data: [] })
	const [modalThresholds, setModalThresholds] = useState(false)
	const [showValues, setShowValues] = useState(false)
	const [showCategories, setShowCategories] = useState(true)

	const filters = useAnalyticsFilters({
		...buildPeriod('7days'),
		pos_id: reduxAccount.pos_id ?? -1,
		order_type_ids: [],
		source_ids: [],
		menu_ids: [],
		category_ids: [],
		detail_value: 'product',
		xyz_interval: 'day',
		tab: 'ABC',
		abc_a: 80,
		abc_b: 15,
		xyz_x: 10,
		xyz_y: 25,
	}, searchWithFilter)
	const { draft, applied, patch, patchApplied } = filters
	const options = useAnalyticsOptions(['sources', 'menus', 'categories'], draft.pos_id)

	async function searchWithFilter(filter) {
		const { tab, ...query } = filter
		const url = buildAnalyticsUrl('/analytics/abc-analysis', query, { is_xyz_enabled: tab !== 'ABC' ? 1 : 0 })
		const response = await GET(url, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	function exportExcel() {
		const rows = (data.data || []).map(item => `<tr><td>${item.menu_name || ''}</td><td>${item.category_name || ''}</td><td>${item.product_name || ''}</td><td>${item.total_quantity}</td><td>${item.abc_quantity}</td><td>${item.total_sales}</td><td>${item.abc_sales}</td><td>${item.total_income}</td><td>${item.abc_income}</td><td>${item.variation_coeff ?? ''}</td><td>${item.xyz_category || ''}</td></tr>`).join('')
		const html = `<table border="1"><thead><tr><th>Меню</th><th>Категория</th><th>Блюдо</th><th>Количество</th><th>ABC</th><th>Выручка</th><th>ABC</th><th>Доход</th><th>ABC</th><th>КВ, %</th><th>XYZ</th></tr></thead><tbody>${rows}</tbody></table>`
		const blob = new Blob([`﻿${html}`], { type: 'application/vnd.ms-excel' })
		const link = document.createElement('a')
		link.href = URL.createObjectURL(blob)
		link.download = `abc-${data.start_date}_${data.end_date}.xls`
		link.click()
		URL.revokeObjectURL(link.href)
	}

	const activeTab = applied.tab
	const showAbc = activeTab === 'ABC' || activeTab === 'ABC-XYZ'
	const showXyz = activeTab === 'XYZ' || activeTab === 'ABC-XYZ'
	// Колонки таблицы — по детализации, с которой построен отчёт
	const detail = data.detail_value || applied.detail_value
	const thresholds = data.thresholds || applied
	const showTwoLevels = showValues && showCategories
	const rowSpanValue = showTwoLevels ? 2 : 1
	const abcA = Number(thresholds.abc_a)
	const abcB = Number(thresholds.abc_b)

	return (
		<>
			<AnalyticsFilterBar
				title="ABC / XYZ анализ блюд"
				icon="uil-chart-bar"
				subtitle="Выручка — с учётом скидки на чек и за вычетом возвратов"
				filters={filters}
				actions={
					<>
						<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={() => setModalThresholds(true)}>
							<i className="uil uil-sliders-v-alt me-1"></i>
							Пороги
						</button>
						<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "10px", fontWeight: 600 }} onClick={exportExcel}>
							<i className="uil uil-file-export me-1"></i>
							Excel
						</button>
					</>
				}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={(tab) => patchApplied({ tab })}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="Меню">
					<FilterSelect isMulti options={options.menus} allLabel="Все меню" value={draft.menu_ids} onChange={(ids) => patch({ menu_ids: ids })} />
				</FilterField>
				<FilterField label="Категория">
					<FilterSelect isMulti options={options.categories} allLabel="Все категории" value={draft.category_ids} onChange={(ids) => patch({ category_ids: ids })} />
				</FilterField>
				<FilterField label="Детализация">
					<FilterSelect options={detailOptions} value={draft.detail_value} onChange={(id) => patch({ detail_value: id })} />
				</FilterField>
				<FilterField label="Интервал XYZ">
					<FilterSelect options={intervalOptions} value={draft.xyz_interval} onChange={(id) => patch({ xyz_interval: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="card" style={cardStyle}>
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap gap-3 mb-3" style={{ lineHeight: "18px" }}>
						<div className="d-flex flex-wrap gap-4">
							{showAbc &&
								<div>
									<div style={mutedStyle} className="mb-1">ABC — доля в накопленном итоге</div>
									<div className="d-flex">
										<div className="text-center bg-success text-white px-2" style={{ minWidth: '120px' }}>A: 0 – {abcA}%</div>
										<div className="text-center bg-warning text-white px-2" style={{ minWidth: '120px' }}>B: {abcA} – {abcA + abcB}%</div>
										<div className="text-center bg-danger text-white px-2" style={{ minWidth: '120px' }}>C: {abcA + abcB} – 100%</div>
									</div>
								</div>
							}
							{showXyz &&
								<div>
									<div style={mutedStyle} className="mb-1">XYZ — коэффициент вариации по интервалу «{(intervalOptions.find(item => item.id === thresholds.xyz_interval) || intervalOptions[0]).name.toLowerCase()}»</div>
									<div className="d-flex">
										<div className="text-center bg-success text-white px-2" style={{ minWidth: '120px' }}>X: ≤ {thresholds.xyz_x}%</div>
										<div className="text-center bg-warning text-white px-2" style={{ minWidth: '120px' }}>Y: {thresholds.xyz_x} – {thresholds.xyz_y}%</div>
										<div className="text-center bg-danger text-white px-2" style={{ minWidth: '120px' }}>Z: &gt; {thresholds.xyz_y}%</div>
									</div>
								</div>
							}
						</div>
						{showAbc &&
							<div className="d-flex flex-column">
								<div className="form-check">
									<input className="form-check-input" type="checkbox" id="abc_show_categories" checked={showCategories} onChange={() => setShowCategories(!showCategories)} />
									<label className="form-check-label" htmlFor="abc_show_categories">Показывать группы</label>
								</div>
								<div className="form-check">
									<input className="form-check-input" type="checkbox" id="abc_show_values" checked={showValues} onChange={() => setShowValues(!showValues)} />
									<label className="form-check-label" htmlFor="abc_show_values">Показывать значения</label>
								</div>
							</div>
						}
					</div>

					{showXyz && data.xyz_note && (
						<div className="alert alert-warning py-2">{data.xyz_note}</div>
					)}

					{showAbc && <AbcSummaryCards summary={data?.abc_summary} />}
					{activeTab === 'ABC-XYZ' && !data.xyz_note && <AbcXyzMatrix rows={data.data || []} />}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th rowSpan={rowSpanValue} style={{ minWidth: '33px' }} className="text-center align-middle">#</th>
									{(detail === 'product' || detail === 'menu') && (
										<th rowSpan={rowSpanValue} style={{ minWidth: '180px' }} className="align-middle">Меню</th>
									)}
									{(detail === 'product' || detail === 'category') && (
										<th rowSpan={rowSpanValue} style={{ minWidth: '180px' }} className="align-middle">Категория</th>
									)}
									{detail === 'product' && (
										<th rowSpan={rowSpanValue} style={{ minWidth: '180px' }} className="align-middle">Блюдо</th>
									)}
									<th rowSpan={rowSpanValue} style={{ width: '100%' }}></th>
									{showAbc && (
										<>
											<th colSpan={rowSpanValue} className="text-center" style={{ minWidth: '140px' }}>Количество</th>
											<th colSpan={rowSpanValue} className="text-center" style={{ minWidth: '140px' }}>Выручка</th>
											<th colSpan={rowSpanValue} className="text-center" style={{ minWidth: '140px' }}>Доход</th>
										</>
									)}
									{showXyz && (
										<>
											<th rowSpan={rowSpanValue} style={{ minWidth: '90px' }} className="text-center align-middle">КВ, %</th>
											<th rowSpan={rowSpanValue} style={{ minWidth: '70px' }} className="text-center align-middle">XYZ</th>
										</>
									)}
								</tr>
								{showTwoLevels && showAbc && (
									<tr>
										<th className="text-center">Значение</th>
										<th className="text-center">ABC</th>
										<th className="text-center">Значение</th>
										<th className="text-center">ABC</th>
										<th className="text-center">Значение</th>
										<th className="text-center">ABC</th>
									</tr>
								)}
							</thead>
							<tbody>
								{(data.data || []).length === 0 && (
									<tr><td colSpan="14" className="text-muted text-center py-4">Нет продаж за выбранный период</td></tr>
								)}
								{(data.data || []).map((item, index) => (
									<tr key={`${item.group_id}-${index}`}>
										<td className="text-center">{index + 1}</td>
										{(detail === 'product' || detail === 'menu') && (
											<td className="td-ellipsis">{item.menu_name || '—'}</td>
										)}
										{(detail === 'product' || detail === 'category') && (
											<td className="td-ellipsis">{item.category_name || 'Без категории'}</td>
										)}
										{detail === 'product' && (
											<td className="td-ellipsis">{item.product_name}</td>
										)}
										<td></td>
										{showAbc && (
											<>
												{showValues && <td className={`text-center ${GROUP_CLASS[item.abc_quantity]}`}>{formatMoney(item.total_quantity)}</td>}
												{showCategories && <td className={`text-center ${GROUP_CLASS[item.abc_quantity]}`}>{item.abc_quantity}</td>}
												{showValues && <td className={`text-center ${GROUP_CLASS[item.abc_sales]}`}>{formatMoney(item.total_sales)}</td>}
												{showCategories && <td className={`text-center ${GROUP_CLASS[item.abc_sales]}`}>{item.abc_sales}</td>}
												{showValues && <td className={`text-center ${GROUP_CLASS[item.abc_income]}`}>{formatMoney(item.total_income)}</td>}
												{showCategories && <td className={`text-center ${GROUP_CLASS[item.abc_income]}`}>{item.abc_income}</td>}
												{!showValues && !showCategories && <td colSpan="3"></td>}
											</>
										)}
										{showXyz && (
											<>
												<td className="text-center">{item.variation_coeff === null || item.variation_coeff === undefined ? '—' : formatMoney(item.variation_coeff, 1)}</td>
												<td className={`text-center ${GROUP_CLASS[item.xyz_category] || ''}`}>{item.xyz_category || '—'}</td>
											</>
										)}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<ThresholdsModal
				show={modalThresholds}
				applied={applied}
				onCancel={() => setModalThresholds(false)}
				onSave={(values) => {
					setModalThresholds(false)
					patchApplied(values)
				}}
			/>
		</>
	)
}

export default Index
